/** Integer arithmetic helpers. Inputs must be safe integers; no floating point is used. */

export function assertSafeInteger(value: number, name: string): void {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`${name} must be a safe integer, got ${value}`);
  }
}

/** Division rounded half away from zero ("commercial" half-up): 2.5 → 3, −2.5 → −3. */
export function divRoundHalfUp(numerator: number, denominator: number): number {
  assertSafeInteger(numerator, 'numerator');
  assertSafeInteger(denominator, 'denominator');
  if (denominator === 0) throw new RangeError('denominator must not be 0');
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  const quotient = Math.floor(n / d);
  const remainder = n - quotient * d;
  const rounded = remainder * 2 >= d ? quotient + 1 : quotient;
  return sign * rounded || 0;
}

/** Division rounded up (ceiling) for non-negative inputs: 9.5 → 10, 9.0 → 9. */
export function divCeil(numerator: number, denominator: number): number {
  assertSafeInteger(numerator, 'numerator');
  assertSafeInteger(denominator, 'denominator');
  if (numerator < 0 || denominator <= 0) {
    throw new RangeError('divCeil expects numerator >= 0 and denominator > 0');
  }
  const quotient = Math.floor(numerator / denominator);
  return quotient * denominator === numerator ? quotient : quotient + 1;
}

/** Percent (up to 2 decimals, e.g. 2.5) → integer basis points (250). */
export function percentToBasisPoints(percent: number): number {
  const bp = Math.round(percent * 100);
  if (!Number.isFinite(percent) || Math.abs(percent * 100 - bp) > 1e-9) {
    throw new RangeError(`percent must have at most 2 decimals, got ${percent}`);
  }
  return bp;
}
